import React, { useEffect, useState, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  Button,
  LinearProgress,
  Typography,
  Box,
  CircularProgress,
  IconButton,
} from '@mui/material'
import { CheckCircle, Close, ErrorOutline } from '@mui/icons-material'
import { useSSE, SSEEvent } from 'contexts/SSEContext'

interface BulkEmailDialogProps {
  open: boolean
  onClose: () => void
  requestId: string | null
  rewardCount: number
}

type DialogState = 'processing' | 'completed' | 'error'

const BulkEmailDialog: React.FC<BulkEmailDialogProps> = ({ open, onClose, requestId, rewardCount }) => {
  const { subscribe } = useSSE()
  const [state, setState] = useState<DialogState>('processing')
  const [progress, setProgress] = useState<{ current: number; total: number }>({ current: 0, total: rewardCount })
  const [successfulEmails, setSuccessfulEmails] = useState<number | null>(null)
  const [failedEmails, setFailedEmails] = useState<number | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const progressPercent = progress.total > 0 ? (progress.current / progress.total) * 100 : 0

  // Handle progress event
  const handleProgressEvent = useCallback(
    (event: SSEEvent) => {
      if (event.data?.requestId === requestId) {
        setProgress({
          current: event.data.processedEmails || 0,
          total: event.data.totalEmails || rewardCount,
        })
      }
    },
    [requestId, rewardCount]
  )

  // Handle ready event
  const handleReadyEvent = useCallback(
    (event: SSEEvent) => {
      if (event.data?.requestId === requestId) {
        setState('completed')
        setSuccessfulEmails(event.data.successfulEmails || 0)
        setFailedEmails(event.data.failedEmails || 0)
        setProgress({
          current: event.data.totalEmails || rewardCount,
          total: event.data.totalEmails || rewardCount,
        })
      }
    },
    [requestId, rewardCount]
  )

  // Handle error event
  const handleErrorEvent = useCallback(
    (event: SSEEvent) => {
      if (event.data?.requestId === requestId) {
        setState('error')
        setErrorMessage(event.data.error || 'An unknown error occurred')
      }
    },
    [requestId]
  )

  // Subscribe to SSE events
  useEffect(() => {
    if (!open || !requestId) return

    const unsubscribeProgress = subscribe('bulk_email_progress', handleProgressEvent)
    const unsubscribeReady = subscribe('bulk_email_ready', handleReadyEvent)
    const unsubscribeError = subscribe('bulk_email_error', handleErrorEvent)

    return () => {
      unsubscribeProgress()
      unsubscribeReady()
      unsubscribeError()
    }
  }, [open, requestId, subscribe, handleProgressEvent, handleReadyEvent, handleErrorEvent])

  // Handle close
  const handleClose = useCallback(() => {
    if (state === 'processing') return
    onClose()
    setTimeout(() => {
      setState('processing')
      setProgress({ current: 0, total: rewardCount })
      setSuccessfulEmails(null)
      setFailedEmails(null)
      setErrorMessage(null)
    }, 300)
  }, [state, onClose, rewardCount])

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown={state === 'processing'}
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: 280,
        },
      }}
    >
      {/* Close button - only enabled when not processing */}
      {state !== 'processing' && (
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'grey.500',
          }}
        >
          <Close />
        </IconButton>
      )}

      <DialogContent sx={{ textAlign: 'center', py: 6, px: 4 }}>
        {/* PROCESSING STATE */}
        {state === 'processing' && (
          <Box>
            <CircularProgress size={60} thickness={4} sx={{ mb: 3 }} />

            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Sending Emails
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              {progress.current} of {progress.total} sent
            </Typography>

            <Box sx={{ px: 2 }}>
              <LinearProgress
                variant="determinate"
                value={progressPercent}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: 'action.hover',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                  },
                }}
              />
            </Box>
          </Box>
        )}

        {/* COMPLETED STATE */}
        {state === 'completed' && (
          <Box>
            <CheckCircle
              sx={{
                fontSize: 64,
                color: 'success.main',
                mb: 2,
              }}
            />

            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Emails Sent
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {successfulEmails} {successfulEmails === 1 ? 'email' : 'emails'} sent successfully
            </Typography>

            {failedEmails !== null && failedEmails > 0 && (
              <Typography variant="body2" color="error" sx={{ mb: 4 }}>
                {failedEmails} {failedEmails === 1 ? 'email' : 'emails'} failed to send
              </Typography>
            )}

            <Button
              variant="contained"
              onClick={handleClose}
              fullWidth
              sx={{
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                mt: 2,
              }}
            >
              Close
            </Button>
          </Box>
        )}

        {/* ERROR STATE */}
        {state === 'error' && (
          <Box>
            <ErrorOutline
              sx={{
                fontSize: 64,
                color: 'error.main',
                mb: 2,
              }}
            />

            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Sending Failed
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {errorMessage || 'An error occurred while sending the emails'}
            </Typography>

            <Button variant="contained" onClick={handleClose} fullWidth sx={{ textTransform: 'none' }}>
              Close
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default BulkEmailDialog
